import React, { useEffect, useMemo, useState } from "react";
import {
  formatDurationMinutes,
  formatDurationValue,
  formatLocalizedDate,
  getSpokenLanguageBadges,
  getUiLanguageMeta,
  getUiText,
} from "./kioskI18n";

const WEEKDAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const weekdayLabel = (weekday, languageCode) => {
  const locale = getUiLanguageMeta(languageCode).locale;
  const index = Math.max(0, WEEKDAY_KEYS.indexOf(weekday));
  const date = new Date(Date.UTC(2026, 0, 4 + index, 12));
  return new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "Europe/Istanbul" }).format(date);
};

const weekdayLabelFromDate = (dateKey, languageCode) => {
  const locale = getUiLanguageMeta(languageCode).locale;
  const [year, month, day] = String(dateKey || "").split("-").map(Number);
  if (!year || !month || !day) return String(dateKey || "");
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    timeZone: "Europe/Istanbul",
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
};

const formatDayMonth = (dateKey, languageCode) => {
  const locale = getUiLanguageMeta(languageCode).locale;
  const [year, month, day] = String(dateKey || "").split("-").map(Number);
  if (!year || !month || !day) return String(dateKey || "");
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Europe/Istanbul",
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
};

const displayReservationDuration = (product, languageCode) => {
  const value = product?.sharedTimeValue || "0";
  return formatDurationValue(value, languageCode, { prefix: false, long: true, zeroAsDuration: true });
};

const getSelectionContextHeading = (languageCode, contextName, type, fallback) => {
  const name = String(contextName || "").trim();
  if (!name) return fallback;
  const code = String(languageCode || "tr").toLowerCase().split("-")[0];
  const translations = {
    tr: {
      personnel: `${name} için personel seçiyorsunuz`,
      time: `${name} için saat seçiyorsunuz`,
      day: `${name} için gün seçiyorsunuz`,
    },
    en: {
      personnel: `You are selecting personnel for ${name}`,
      time: `You are selecting a time for ${name}`,
      day: `You are selecting a day for ${name}`,
    },
    ru: {
      personnel: `Вы выбираете специалиста для ${name}`,
      time: `Вы выбираете время для ${name}`,
      day: `Вы выбираете день для ${name}`,
    },
    de: {
      personnel: `Sie wählen Personal für ${name}`,
      time: `Sie wählen eine Uhrzeit für ${name}`,
      day: `Sie wählen einen Tag für ${name}`,
    },
    ar: {
      personnel: `أنت تختار الموظف لخدمة ${name}`,
      time: `أنت تختار الوقت لخدمة ${name}`,
      day: `أنت تختار اليوم لخدمة ${name}`,
    },
  };
  return translations[code]?.[type] || `${name} · ${fallback}`;
};

export function AppointmentDurationBanner({
  totalMinutes = 0,
  totalDays = 0,
  parts = [],
  languageCode = "tr",
  className = "",
  compact = false,
  showTotalSentence = false,
}) {
  const ui = getUiText(languageCode);
  const meta = getUiLanguageMeta(languageCode);
  const dayText = totalDays > 0
    ? formatDurationValue(`${totalDays}d`, languageCode, { prefix: false, long: true })
    : "";
  const minuteText = totalMinutes > 0
    ? formatDurationMinutes(totalMinutes, languageCode, { prefix: false, zeroAsDuration: true })
    : "";
  const durationText = [dayText, minuteText].filter(Boolean).join(" + ") ||
    formatDurationMinutes(0, languageCode, { prefix: false, zeroAsDuration: true });
  const cleanParts = (Array.isArray(parts) ? parts : []).filter(Boolean);

  return (
    <div
      dir={meta.dir}
      className={`min-w-0 rounded-3xl border-4 border-orange-300 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 shadow-xl ${compact ? "px-3 py-2.5" : "px-7 py-6"} ${className}`}
    >
      <p
        className={`${compact ? "text-[12px] leading-[1.08]" : "text-xl"} break-words font-extrabold text-amber-950`}
      >
        {ui.selectedServicesTotalDuration}
      </p>
      <p
        className={`${
          compact
            ? "mt-1 flex min-h-[42px] items-center justify-center break-words text-[22px] leading-[1.02]"
            : "mt-2 text-5xl"
        } font-black text-orange-600`}
      >
        {durationText}
      </p>
      {cleanParts.length > 0 && (
        <p
          className={`${
            compact
              ? "mt-1 line-clamp-3 text-[9px] leading-[1.08]"
              : "mt-3 text-lg leading-relaxed"
          } min-w-0 break-words font-bold text-gray-700`}
        >
          {showTotalSentence
            ? `${cleanParts.join(" + ")} = ${ui.appointmentWillLast} ${durationText}.`
            : cleanParts.join(" + ")}
        </p>
      )}
    </div>
  );
}

export function ProductPreviewModal({ product, open, onClose, onAdd, languageCode = "tr" }) {
  const ui = getUiText(languageCode);
  const meta = getUiLanguageMeta(languageCode);
  const showOffersInsteadOfPrice = !!product?.showOffersInsteadOfPrice;
  const originalPreviewPrice = Number(product?.basePrice ?? product?.price ?? 0);
  const previewDiscountRate = Math.min(100, Math.max(0, Number(product?.discountRate || 0)));
  const discountedPreviewPrice = product?.discountEnabled
    ? Math.round((originalPreviewPrice * (1 - previewDiscountRate / 100) + Number.EPSILON) * 100) / 100
    : originalPreviewPrice;
  const hasSelectableOptions = Array.isArray(product?.questions) &&
    product.questions.some((question) => Array.isArray(question?.options) && question.options.length > 0);
  const hasDurationOptions = Array.isArray(product?.questions) &&
    product.questions.some((question) =>
      Array.isArray(question?.options) &&
      question.options.some((option) => !!option?.durationEnabled && String(option?.durationValue || "").trim() !== "")
    );
  const baseDurationValue = String(product?.sharedTimeValue || "0").trim().toLowerCase();
  const durationDeterminedAfterSelection =
    hasSelectableOptions && hasDurationOptions && (!baseDurationValue || baseDurationValue === "0");

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !product) return null;

  return (
    <div className="fixed inset-0 z-[12000] bg-black/80 backdrop-blur-md flex items-center justify-center p-5" dir={meta.dir}>
      <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-[32px] bg-white shadow-2xl">
        <div className="relative aspect-video bg-black rounded-t-[32px] overflow-hidden">
          {product.videoUrl ? (
            <video
              key={`${product.videoId || product.uniqcode}_${product.videoVersion || 1}`}
              src={product.videoUrl}
              className="h-full w-full object-contain"
              autoPlay
              controls
              playsInline
              preload="auto"
            />
          ) : (
            <img src={product.image} alt={product.name} className="h-full w-full object-contain" />
          )}
          <button type="button" onClick={onClose} className="absolute right-5 top-5 h-14 w-14 rounded-full bg-black/65 text-white text-3xl font-bold">×</button>
        </div>

        <div className="p-8">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h2 className="text-4xl font-extrabold text-gray-950">{product.name}</h2>
              <p className="mt-3 text-xl leading-relaxed text-gray-700">{product.videoDescription || product.description || product.desc || ""}</p>
            </div>
            <div className="shrink-0 rounded-full bg-emerald-50 px-6 py-3 text-2xl font-extrabold text-emerald-700">
              {showOffersInsteadOfPrice ? (
                ui.viewOffers
              ) : product?.discountEnabled && previewDiscountRate > 0 ? (
                <div className="text-right leading-tight">
                  <div className="text-sm font-bold text-gray-400 line-through">
                    ₺{originalPreviewPrice.toLocaleString(meta.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div>
                    ₺{discountedPreviewPrice.toLocaleString(meta.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · %{previewDiscountRate}
                  </div>
                </div>
              ) : (
                `₺${discountedPreviewPrice.toLocaleString(meta.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              )}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between gap-6 rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-100 px-7 py-5 shadow-md">
            <p className="text-2xl font-extrabold text-amber-950">{ui.serviceDuration}</p>
            <p className={`${durationDeterminedAfterSelection ? "text-2xl" : "text-4xl"} max-w-[55%] text-right font-black leading-tight text-orange-600`}>
              {durationDeterminedAfterSelection ? ui.durationAfterSelection : displayReservationDuration(product, languageCode)}
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-5">
            <button type="button" onClick={onClose} className="rounded-2xl bg-gray-200 py-5 text-2xl font-bold text-gray-800">{ui.cancel}</button>
            <button type="button" onClick={onAdd} className="rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 py-5 text-2xl font-extrabold text-white shadow-xl">{ui.add} →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpokenLanguages({ values, languageCode, large = false, compact = false }) {
  const ui = getUiText(languageCode);
  const badges = getSpokenLanguageBadges(values);
  if (!badges.length) return null;
  return (
    <div className={compact ? "mt-1" : "mt-4"}>
      {!compact && <p className={`${large ? "text-lg" : "text-xs"} font-bold text-gray-600 mb-2`}>{ui.spokenLanguages}</p>}
      <div className={`flex flex-wrap ${compact ? "gap-1" : "gap-2"}`}>
        {badges.map((badge) => (
          <span key={badge.code} className={`${large ? "px-4 py-2 text-base" : compact ? "px-1.5 py-0.5 text-[9px]" : "px-2.5 py-1 text-xs"} inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 font-bold text-blue-900`}>
            <img src={badge.flagImage} alt="" className={`${large ? "h-7 w-10" : compact ? "h-3 w-5" : "h-5 w-8"} rounded object-cover shadow-sm`} />
            {!compact && <span>{badge.label}</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

export function PersonnelScreen({ personnel = [], onBack, onChoose, contextName = "", languageCode = "tr", loading = false }) {
  const [preview, setPreview] = useState(null);
  const ui = getUiText(languageCode);
  const meta = getUiLanguageMeta(languageCode);
  const count = personnel.length;

  // Eski kart tasarımı korunur: fotoğraf her zaman 9:16, bilgiler fotoğrafın altında.
  // Yalnızca personel sayısı arttıkça sütun sayısı ve iç boşluklar kontrollü biçimde ayarlanır.
  const layout = count > 20
    ? { columns: 7, gap: 8, padding: "p-2.5", title: "text-base", detail: "text-xs", compactLanguages: true, contentHeight: "min-h-[120px]" }
    : count > 15
      ? { columns: 6, gap: 9, padding: "p-3", title: "text-lg", detail: "text-xs", compactLanguages: true, contentHeight: "min-h-[140px]" }
      : count > 10
        ? { columns: 5, gap: 12, padding: "p-4", title: "text-xl", detail: "text-sm", compactLanguages: false, contentHeight: "min-h-[190px]" }
        : count > 6
          ? { columns: 4, gap: 16, padding: "p-5", title: "text-2xl", detail: "text-sm", compactLanguages: false, contentHeight: "min-h-[225px]" }
          : { columns: Math.max(1, Math.min(3, count || 1)), gap: 18, padding: "p-5", title: "text-2xl", detail: "text-sm", compactLanguages: false, contentHeight: "min-h-[250px]" };

  return (
    <div className="h-screen overflow-hidden bg-slate-100" dir={meta.dir}>
      <header className="h-[126px] bg-white/95 backdrop-blur border-b px-8 py-5 flex items-center justify-between">
        <button type="button" onClick={onBack} className="rounded-2xl bg-gray-200 px-7 py-4 text-xl font-bold">← {ui.back}</button>
        <div className="text-center">
          <h1 className="text-4xl font-extrabold text-gray-950">{getSelectionContextHeading(languageCode, contextName, "personnel", ui.selectPersonnel)}</h1>
          <p className="text-gray-600 mt-1">{ui.personnelSubtitle}</p>
        </div>
        <div className="w-28" />
      </header>

      <main className="relative h-[calc(100vh-126px)] overflow-hidden p-4">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <div className="rounded-3xl bg-white px-12 py-10 text-center shadow-xl">
              <div className="mx-auto h-14 w-14 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
              <p className="mt-5 text-2xl font-extrabold text-gray-800">{ui.availabilityLoading}</p>
            </div>
          </div>
        ) : personnel.length ? (
          <div
            className="grid h-full content-start justify-items-start overflow-hidden"
            style={{
              gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
              gap: `${layout.gap}px`,
            }}
          >
            {personnel.map((person) => {
              const fullDates = Array.isArray(person?.reservationOverview?.fullDates)
                ? person.reservationOverview.fullDates
                : [];
              return (
                <button
                  type="button"
                  key={person.personelId}
                  onClick={() => setPreview(person)}
                  className="group relative flex w-full max-w-[300px] min-h-0 flex-col overflow-hidden rounded-3xl bg-white text-left shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-2xl"
                >
                  <div className="absolute left-2 right-2 top-2 z-10 flex flex-wrap gap-1">
                    {fullDates.map((dateKey) => (
                      <span key={dateKey} className="rounded-full bg-red-600/95 px-2 py-1 text-[11px] font-black text-white shadow">
                        {weekdayLabelFromDate(dateKey, languageCode)} {ui.full}
                      </span>
                    ))}
                  </div>

                  <div
                    className="relative w-full shrink-0 overflow-hidden bg-gray-200"
                    style={{ aspectRatio: "9 / 16" }}
                  >
                    <img
                      src={person.coverPhotoUrl}
                      alt={person.firstName}
                      width={900}
                      height={1600}
                      className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="eager"
                      decoding="async"
                    />
                  </div>

                  <div className={`${layout.padding} ${layout.contentHeight} flex flex-1 flex-col`}>
                    <h2 className={`${layout.title} line-clamp-2 font-extrabold leading-tight text-gray-950`}>
                      {person.firstName || ui.personnel}
                    </h2>
                    <p className={`mt-1 line-clamp-2 ${layout.detail} font-semibold text-emerald-700`}>
                      {person.expertise || ""}
                    </p>
                    <p className={`mt-2 line-clamp-1 ${layout.detail} text-gray-500`}>
                      {person.experience || ""}
                    </p>
                    <div className={layout.compactLanguages ? "mt-auto min-h-[32px]" : "mt-auto min-h-[82px]"}>
                      <SpokenLanguages
                        values={person.spokenLanguages}
                        languageCode={languageCode}
                        compact={layout.compactLanguages}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rounded-3xl bg-white p-16 text-center text-3xl font-bold text-red-600 shadow">{ui.noPersonnel}</div>
        )}
      </main>

      {preview && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/80 p-5 backdrop-blur-md">
          <div className="relative grid w-full max-w-6xl max-h-[96vh] overflow-hidden rounded-[36px] bg-white shadow-2xl lg:grid-cols-[minmax(390px,500px)_1fr]">
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute right-5 top-5 z-30 h-14 w-14 rounded-full bg-black/75 text-3xl font-bold text-white shadow-xl"
            >
              ×
            </button>
            <div className="relative flex items-center justify-center bg-black">
              <div className="relative aspect-[9/16] h-[90vh] max-h-[980px] w-auto max-w-full overflow-hidden bg-black">
                {preview.videoUrl ? (
                  <video key={`${preview.personelId}_${preview.videoVersion || 1}`} src={preview.videoUrl} autoPlay controls playsInline preload="auto" className="h-full w-full object-contain" />
                ) : (
                  <img src={preview.coverPhotoUrl} alt={preview.firstName} className="h-full w-full object-cover" />
                )}
              </div>
            </div>
            <div className="flex flex-col justify-center overflow-y-auto p-10">
              <h2 className="text-5xl font-extrabold">{preview.firstName}</h2>
              <p className="mt-5 text-3xl font-semibold text-emerald-700">{preview.expertise}</p>
              <p className="mt-4 text-2xl text-gray-600">{ui.experience}: {preview.experience || ui.notSpecified}</p>
              <SpokenLanguages values={preview.spokenLanguages} languageCode={languageCode} large />
              <button
                type="button"
                onClick={() => {
                  const picked = preview;
                  setPreview(null);
                  onChoose?.(picked);
                }}
                className="mt-10 w-full rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 py-6 text-3xl font-extrabold text-white shadow-xl"
              >
                {ui.choosePersonnel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AvailabilityScreen({ data, loading, error, onBack, onChoose, totalDurationMinutes = 0, totalDurationDays = 0, durationParts = [], contextName = "", languageCode = "tr" }) {
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const ui = getUiText(languageCode);
  const meta = getUiLanguageMeta(languageCode);
  const days = Array.isArray(data?.days) ? data.days : [];
  const activeDay = days.find((day) => day.date === selectedDay) || days.find((day) => day.slots?.length) || days[0] || null;

  useEffect(() => {
    if (!selectedDay && activeDay?.date) setSelectedDay(activeDay.date);
  }, [activeDay?.date, selectedDay]);

  useEffect(() => {
    setSelectedSlot(null);
  }, [selectedDay]);

  return (
    <div className="min-h-screen bg-slate-100 pb-24" dir={meta.dir}>
      <header className="sticky top-0 z-20 flex items-center justify-between border-b bg-white/95 px-8 py-6 backdrop-blur">
        <button type="button" onClick={onBack} className="rounded-2xl bg-gray-200 px-7 py-4 text-xl font-bold">← {ui.personnel}</button>
        <div className="text-center">
          <h1 className="text-4xl font-extrabold">{getSelectionContextHeading(languageCode, contextName, "time", ui.dateTimeSelect)}</h1>
          <p className="mt-1 text-gray-600">{ui.istanbulTime} · {ui.earliest} {data?.istanbulNow?.nextQuarter || "--:--"}</p>
        </div>
        <div className="w-32" />
      </header>

      <main className="mx-auto max-w-7xl p-8">
        <AppointmentDurationBanner
          totalMinutes={totalDurationMinutes}
          totalDays={totalDurationDays}
          parts={durationParts}
          languageCode={languageCode}
          className="mb-8"
        />
        {loading && <div className="rounded-3xl bg-white p-16 text-center text-3xl font-bold shadow">{ui.availabilityLoading}</div>}
        {error && <div className="mb-6 rounded-2xl border-2 border-red-300 bg-red-50 p-6 text-xl font-bold text-red-700">{error}</div>}
        {!loading && (
          <>
            <div className="grid grid-cols-4 gap-3 lg:grid-cols-7">
              {days.map((day) => {
                const disabled = !day.working || !day.slots?.length;
                const active = day.date === activeDay?.date;
                return (
                  <button type="button" key={day.date} disabled={disabled} onClick={() => setSelectedDay(day.date)} className={`rounded-2xl border-2 px-3 py-4 text-center transition ${disabled ? "border-red-200 bg-red-50 text-red-400" : active ? "border-emerald-600 bg-emerald-100 text-emerald-950 shadow-lg" : "border-gray-200 bg-white text-gray-800"}`}>
                    <span className="block text-sm font-bold capitalize">{weekdayLabel(day.weekday, languageCode)}</span>
                    <span className="mt-1 block text-xs">{formatDayMonth(day.date, languageCode)}</span>
                    <span className="mt-2 block text-xs font-semibold">
                      {disabled
                        ? (day.working ? ui.notAvailable : ui.notWorking)
                        : `${formatDurationMinutes(day.slots.length * 15, languageCode, { prefix: false, zeroAsDuration: true })} ${ui.availableSlots}`}
                    </span>
                  </button>
                );
              })}
            </div>

            {activeDay && (
              <section className="mt-8 rounded-3xl bg-white p-8 shadow-xl">
                <h2 className="text-3xl font-extrabold">{formatLocalizedDate(activeDay.date, languageCode)}</h2>
                <p className="mt-2 text-gray-600">{ui.working}: {activeDay.intervals?.map((i) => `${i.start}–${i.end}`).join(", ") || ui.notWorking}</p>
                <div className="mt-6 grid grid-cols-3 gap-4 md:grid-cols-5 lg:grid-cols-7">
                  {(activeDay.slots || []).map((slot) => {
                    const active = selectedSlot?.startAt === slot.startAt && selectedSlot?.endAt === slot.endAt;
                    return (
                      <button type="button" key={`${slot.startAt}_${slot.endAt}`} onClick={() => setSelectedSlot({ ...slot, date: activeDay.date })} className={`rounded-2xl px-4 py-5 text-white shadow-lg transition hover:scale-105 ${active ? "bg-gradient-to-br from-orange-500 to-amber-500 ring-4 ring-orange-200" : "bg-gradient-to-br from-emerald-500 to-teal-600"}`}>
                        <span className="block text-2xl font-extrabold">{slot.start}</span>
                        <span className="mt-1 block text-sm font-semibold">{slot.end} {ui.until}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {selectedSlot && (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-black/65 p-6 backdrop-blur-sm">
          <div className="w-full max-w-3xl rounded-[34px] border-4 border-emerald-300 bg-white p-10 text-center shadow-2xl">
            <p className="text-4xl font-black text-emerald-900">
              {ui.appointmentBetween.replace("{start}", selectedSlot.start).replace("{end}", selectedSlot.end)}
            </p>
            <p className="mt-4 text-2xl font-bold text-gray-700">
              {ui.appointmentLength}: {formatDurationMinutes(totalDurationMinutes, languageCode, { prefix: false, zeroAsDuration: true })}
            </p>
            <div className="mt-8 grid grid-cols-2 gap-5">
              <button type="button" onClick={() => setSelectedSlot(null)} className="rounded-2xl bg-gray-200 py-5 text-2xl font-extrabold text-gray-800">
                {ui.cancel}
              </button>
              <button type="button" onClick={() => onChoose?.(selectedSlot)} className="rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 py-5 text-2xl font-extrabold text-white shadow-xl">
                {ui.confirmTime}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const addDays = (dateKey, count) => {
  const [y, m, d] = String(dateKey).split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + count, 12));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
};

export function DayReservationScreen({ durationDays, serverNow, onBack, onChoose, contextName = "", languageCode = "tr" }) {
  const ui = getUiText(languageCode);
  const meta = getUiLanguageMeta(languageCode);
  const today = useMemo(() => {
    const formatter = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul", year: "numeric", month: "2-digit", day: "2-digit" });
    return formatter.format(serverNow ? new Date(serverNow) : new Date());
  }, [serverNow]);
  const [selected, setSelected] = useState(today);
  const safeDays = Math.max(1, Number(durationDays || 1));
  const endDate = addDays(selected, safeDays - 1);
  const options = useMemo(() => Array.from({ length: 60 }, (_, index) => addDays(today, index)), [today]);
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 p-8" dir={meta.dir}>
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between">
          <button type="button" onClick={onBack} className="rounded-2xl bg-white px-7 py-4 text-xl font-bold shadow">← {ui.back}</button>
          <div className="text-center">
            <h1 className="text-4xl font-extrabold">{getSelectionContextHeading(languageCode, contextName, "day", ui.selectStartDay)}</h1>
            <p className="mt-2 text-xl text-gray-700">{ui.totalDuration}: {safeDays} {ui.day}</p>
          </div>
          <div className="w-28" />
        </div>
        <div className="mt-10 grid grid-cols-4 md:grid-cols-6 gap-4">
          {options.map((dateKey) => (
            <button type="button" key={dateKey} onClick={() => setSelected(dateKey)} className={`rounded-2xl border-2 p-4 text-center ${selected === dateKey ? "border-orange-600 bg-orange-500 text-white shadow-xl" : "border-orange-200 bg-white text-gray-800"}`}>
              <span className="block text-sm font-bold">{formatLocalizedDate(dateKey, languageCode).split(" ")[0]}</span>
              <span className="block mt-1 text-xs">{dateKey.slice(5)}</span>
            </button>
          ))}
        </div>
        <div className="mt-10 rounded-3xl bg-white p-8 shadow-xl text-center">
          <p className="text-xl text-gray-600">{ui.selectedPeriod}</p>
          <p className="mt-2 text-3xl font-extrabold text-orange-700">{formatLocalizedDate(selected, languageCode)} – {formatLocalizedDate(endDate, languageCode)}</p>
          <p className="mt-3 text-lg text-gray-600">{ui.continuousDays}: {safeDays} {ui.day}.</p>
          <button type="button" onClick={() => onChoose?.({ startDate: selected, endDate, durationDays: safeDays })} className="mt-8 w-full rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 py-6 text-3xl font-extrabold text-white shadow-xl">{ui.chooseDays}</button>
        </div>
      </div>
    </div>
  );
}
